// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import {
  addFields as addFieldsAction,
  getFields as getFieldsAction,
  removeFields as removeFieldsAction,
  getMetadataDefinitions as getMetadataDefinitionsAction,
  getMetadataMappings as getMetadataMappingsAction,
} from 'state/commands/catalogCommands';
import { actions as catalogActions } from 'state/reducers/catalogReducer';
import { showDialogPanel } from 'state/commands/dialogPanelCommands';
import {
  getActivePanelId,
  getFields,
  getMetadataDefinitions,
  getMetadataMappings,
  getSelectedFieldCategory,
  getSelectedFieldsListItemName,
} from 'state/selectors';
import type {
  Catalog,
  Dispatch,
  FieldDefinition,
  FieldsListItem,
  MetadataDefinition,
  MetadataMapping,
} from 'state/types';
import {
  displayNameForField,
  fieldDefForFieldsListItemName,
  FIELDS_CATEGORIES,
  fieldsListItemsForCategory,
  isFieldRemovable,
} from 'util/fieldsPageUtils';
import localizedStrings from 'util/localizedStrings';
import { bindActionCreatorsSafe } from 'util/safeDispatch';
import FieldForm from './FieldForm';
import CreateFieldDialog from './CreateFieldDialog';
import MetadataDefinitionsDialog from './MetadataDefinitionsDialog';
import CreateMetadataDefinitionDialog from './CreateMetadataDefinitionDialog';
import XMPNamespacesDialog from './XMPNamespacesDialog';
import CreateXMPNamespaceDialog from './CreateXMPNamespaceDialog';
import PageHeader from 'view/Pages/PageHeader';
import {
  CREATE_FIELD_PANEL_ID,
  CREATE_METADATA_PANEL_ID,
  CREATE_XMP_NAMESPACE_PANEL_ID,
  METADATA_PANEL_ID,
  XMP_NAMESPACE_PANEL_ID,
} from 'util/constants';
import logger from 'portfolio-shared/util/logging';

const {
  setSelectedFieldCategory,
  setSelectedFieldsListItemName,
} = catalogActions;

type Props = {
  activePanelId: ?string,
  addFieldsAction: typeof addFieldsAction,
  catalog: Catalog,
  category: ?string,
  fields: FieldDefinition[],
  getFieldsAction: typeof getFieldsAction,
  getMetadataDefinitionsAction: typeof getMetadataDefinitionsAction,
  getMetadataMappingsAction: typeof getMetadataMappingsAction,
  intl: IntlShape,
  metadataDefinitions: MetadataDefinition[],
  metadataMappings: MetadataMapping[],
  removeFieldsAction: typeof removeFieldsAction,
  selectedFieldsListItemName: ?string,
  setSelectedFieldCategory: typeof setSelectedFieldCategory,
  setSelectedFieldsListItemName: typeof setSelectedFieldsListItemName,
  showDialogPanel: (string, ?Object) => void,
};

class FieldsPage extends Component<Props> {
  displayNameForFieldListItem: Function;
  getFieldsListItems: Function;
  handleChangeCategory: Function;
  handleClickAdd: Function;
  handleClickMetadataDefinitions: Function;
  handleClickNamespaceDefinitions: Function;
  handleDeleteField: Function;
  handleKeyDown: Function;
  handleSelectFieldsListItem: Function;
  handleToggleFieldEnabled: Function;
  isFieldRemovable: Function;

  constructor() {
    super();
    this.displayNameForFieldListItem = this.displayNameForFieldListItem.bind(
      this
    );
    this.getFieldsListItems = this.getFieldsListItems.bind(this);
    this.handleChangeCategory = this.handleChangeCategory.bind(this);
    this.handleClickAdd = this.handleClickAdd.bind(this);
    this.handleClickMetadataDefinitions = this.handleClickMetadataDefinitions.bind(
      this
    );
    this.handleClickNamespaceDefinitions = this.handleClickNamespaceDefinitions.bind(
      this
    );
    this.handleDeleteField = this.handleDeleteField.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSelectFieldsListItem = this.handleSelectFieldsListItem.bind(
      this
    );
    this.handleToggleFieldEnabled = this.handleToggleFieldEnabled.bind(this);
    this.isFieldRemovable = this.isFieldRemovable.bind(this);
  }

  componentDidMount() {
    this.props.getFieldsAction(this.props.catalog.catalogId);
    this.props.getMetadataDefinitionsAction();
    this.props.getMetadataMappingsAction(this.props.catalog.catalogId);
  }

  handleClickAdd() {
    this.props.showDialogPanel(CREATE_FIELD_PANEL_ID);
  }

  handleClickMetadataDefinitions() {
    this.props.showDialogPanel(METADATA_PANEL_ID);
  }

  handleClickNamespaceDefinitions() {
    this.props.showDialogPanel(XMP_NAMESPACE_PANEL_ID);
  }

  handleSelectFieldsListItem(e: SyntheticMouseEvent<>, name: string) {
    this.props.setSelectedFieldsListItemName(name);
  }

  displayNameForFieldListItem(fli: FieldsListItem) {
    return displayNameForField(this.props.intl, fli.name);
  }

  handleKeyDown(e: SyntheticKeyboardEvent<>) {
    const fieldListItems = this.getFieldsListItems();
    let fli;
    if (!e.metaKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      const currentIndex = this.getSelectedIndex();
      if (e.key === 'ArrowUp' && currentIndex > 0) {
        fli = fieldListItems[currentIndex - 1];
      } else if (
        e.key === 'ArrowDown' &&
        currentIndex < fieldListItems.length - 1
      ) {
        fli = fieldListItems[currentIndex + 1];
      }
    } else if (!e.metaKey && /[a-z,0-9]/i.test(e.key)) {
      fli = fieldListItems.find(
        f =>
          this.displayNameForFieldListItem(f)
            .substr(0, 1)
            .localeCompare(e.key, undefined, {
              usage: 'search',
              sensitivity: 'base',
            }) === 0
      );
    }

    if (fli) {
      this.props.setSelectedFieldsListItemName(fli.name);
      var elem = document.getElementById(`fli-${fli.name}`);
      elem && elem.scrollIntoView();
      e.preventDefault();
    }
  }

  // Return sorted FLIs for the current category
  getFieldsListItems() {
    const { category, fields, metadataDefinitions } = this.props;
    if (!category) {
      return [];
    }
    const flis: FieldsListItem[] = fieldsListItemsForCategory(
      fields,
      metadataDefinitions,
      category
    );
    const sortedFlis = flis.sort((a, b) =>
      this.displayNameForFieldListItem(a).localeCompare(
        this.displayNameForFieldListItem(b)
      )
    );
    return sortedFlis;
  }

  getSelectedIndex() {
    const { selectedFieldsListItemName } = this.props;
    const ix = this.getFieldsListItems().findIndex(
      fli => fli.name === selectedFieldsListItemName
    );
    return ix;
  }

  handleChangeCategory(e) {
    this.props.setSelectedFieldsListItemName(null);
    this.props.setSelectedFieldCategory(e.target.value);
  }

  handleDeleteField() {
    const { catalog } = this.props;
    const fieldListItems = this.getFieldsListItems();
    const currentIndex = this.getSelectedIndex();
    const selectedFieldsListItem = fieldListItems[currentIndex];

    if (selectedFieldsListItem) {
      this.props.removeFieldsAction(catalog.catalogId, [
        selectedFieldsListItem.fieldDef.id,
      ]);
    }
  }

  isFieldRemovable() {
    const { metadataDefinitions } = this.props;
    const fieldListItems = this.getFieldsListItems();
    const currentIndex = this.getSelectedIndex();
    const selectedFieldsListItem = fieldListItems[currentIndex];
    if (selectedFieldsListItem && selectedFieldsListItem.fieldDef) {
      return isFieldRemovable(
        selectedFieldsListItem.fieldDef,
        metadataDefinitions
      );
    }
    return false;
  }

  handleToggleFieldEnabled() {
    const {
      addFieldsAction,
      catalog,
      removeFieldsAction,
      fields,
      metadataDefinitions,
    } = this.props;
    const fieldListItems = this.getFieldsListItems();
    const currentIndex = this.getSelectedIndex();
    const selectedFieldsListItem = fieldListItems[currentIndex];
    const mdId = selectedFieldsListItem.metadataDefinition.id;
    const fieldDef: ?FieldDefinition = fieldDefForFieldsListItemName(
      selectedFieldsListItem.internalName,
      metadataDefinitions,
      fields
    );
    if (!fieldDef) {
      logger.warn(
        'Could not get a field def for the fields list item ' +
          selectedFieldsListItem.internalName
      );
      return;
    }

    if (selectedFieldsListItem.isEnabled) {
      removeFieldsAction(catalog.catalogId, [fieldDef.id], true);
    } else {
      addFieldsAction(catalog.catalogId, [fieldDef], mdId);
    }
  }

  render() {
    const {
      catalog: { catalogId },
      activePanelId,
      category,
      intl: { formatMessage },
      selectedFieldsListItemName,
    } = this.props;

    const fieldsListItems = this.getFieldsListItems();

    return (
      <div className="page-contents" data-test-name="catalog-fields-page">
        {activePanelId === CREATE_FIELD_PANEL_ID && (
          <CreateFieldDialog catalogId={catalogId} />
        )}
        {activePanelId === METADATA_PANEL_ID && <MetadataDefinitionsDialog />}
        {activePanelId === CREATE_METADATA_PANEL_ID && (
          <CreateMetadataDefinitionDialog />
        )}
        {activePanelId === XMP_NAMESPACE_PANEL_ID && <XMPNamespacesDialog />}
        {activePanelId === CREATE_XMP_NAMESPACE_PANEL_ID && (
          <CreateXMPNamespaceDialog />
        )}

        <div className="page-contents columns is-gapless">
          <div className="column is-one-third page">
            <header className="fields-page-header">
              <PageHeader
                isSecondary={true}
                deleteDisabled={!this.isFieldRemovable()}
                onClickAdd={this.handleClickAdd}
                onClickDelete={this.handleDeleteField}
                title={formatMessage(
                  localizedStrings.CATALOG_INGEST_FIELDS_LABEL
                )}
              />
            </header>

            <div className="page bordered-page-column">
              <div className="fields-page-select-container">
                <div className="select is-fullwidth">
                  <select
                    className="select"
                    onChange={this.handleChangeCategory}
                    value={category}
                    data-test-name="select-category-select"
                  >
                    {FIELDS_CATEGORIES.map(category => (
                      <option
                        key={category.id}
                        value={category.id}
                        data-test-name={
                          'field-category-' +
                          this.props.intl.formatMessage(category.label)
                        }
                      >
                        {formatMessage(category.label)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div
                className="is-scrollable"
                onKeyDown={this.handleKeyDown}
                tabIndex="0"
              >
                <table className="table is-hoverable is-fullwidth">
                  <tbody>
                    {fieldsListItems.map((fli, index) => (
                      <FieldsListRenderer
                        canBeDisabled={fli.canBeDisabled}
                        hasCheckbox={fli.hasCheckbox}
                        isEnabled={fli.isEnabled}
                        name={this.displayNameForFieldListItem(fli)}
                        internalName={fli.name}
                        index={index}
                        isSelected={
                          !!selectedFieldsListItemName &&
                          fli.name === selectedFieldsListItemName
                        }
                        key={fli.name}
                        onClick={this.handleSelectFieldsListItem}
                        onToggleFieldEnabled={this.handleToggleFieldEnabled}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="column page">
            <header className="fields-page-header">
              <div className="fields-page-header-button-group buttons buttonsGroup is-pulled-right">
                <a
                  className="button"
                  data-test-name="manage-metadata"
                  onClick={this.handleClickMetadataDefinitions}
                >
                  <FormattedMessage
                    {...localizedStrings.FIELD_ACTION_MANAGE_METADATA}
                  />
                </a>
                <a
                  className="button"
                  data-test-name="manage-xmp-namespaces"
                  onClick={this.handleClickNamespaceDefinitions}
                >
                  <FormattedMessage
                    {...localizedStrings.FIELD_ACTION_MANAGE_CUSTOM_XMP_NAMESPACES}
                  />
                </a>
              </div>
            </header>

            <FieldForm catalog={this.props.catalog} />
          </div>
        </div>
      </div>
    );
  }
}

type FieldsListRendererProps = {
  canBeDisabled: boolean,
  hasCheckbox: boolean,
  index: number,
  isEnabled: boolean,
  isSelected: boolean,
  name: string,
  internalName: string,
  onClick: (SyntheticMouseEvent<>, string) => void,
  onToggleFieldEnabled: string => void,
};

class FieldsListRenderer extends Component<FieldsListRendererProps> {
  handleChangeCheckbox: Function;
  handleClick: Function;

  constructor() {
    super();
    this.handleChangeCheckbox = this.handleChangeCheckbox.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  handleClick(e: SyntheticMouseEvent<>) {
    this.props.onClick(e, this.props.internalName);
  }

  handleChangeCheckbox() {
    this.props.onToggleFieldEnabled(this.props.internalName);
  }

  render() {
    const {
      canBeDisabled,
      hasCheckbox,
      isEnabled,
      isSelected,
      name,
    } = this.props;
    const className = `has-pointer ${isSelected ? 'is-selected' : ''}`;
    const checkClassName = `is-checkradio is-small ${
      isSelected ? ' has-background-color is-white' : ''
    }`;
    const checkboxId = `enabled-${name}`;
    return (
      <tr
        className={className}
        id={`fli-${name}`}
        onClick={this.handleClick}
        data-test-name={'field-name-' + name}
      >
        <td>
          <div style={{ display: 'flex' }}>
            {hasCheckbox && (
              <div>
                {canBeDisabled && (
                  <div className="control">
                    <input
                      className={checkClassName}
                      data-test-name="enable-field-checkbox"
                      id={checkboxId}
                      type="checkbox"
                      checked={isEnabled}
                      onChange={this.handleChangeCheckbox}
                    />
                    <label htmlFor={checkboxId} />
                  </div>
                )}
              </div>
            )}
            {name}
          </div>
        </td>
      </tr>
    );
  }
}

export default connect(
  state => ({
    activePanelId: getActivePanelId(state),
    category: getSelectedFieldCategory(state),
    fields: getFields(state),
    metadataDefinitions: getMetadataDefinitions(state),
    metadataMappings: getMetadataMappings(state),
    selectedFieldsListItemName: getSelectedFieldsListItemName(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsSafe({
      addFieldsAction,
      getFieldsAction,
      getMetadataDefinitionsAction,
      getMetadataMappingsAction,
      removeFieldsAction,
      setSelectedFieldCategory,
      setSelectedFieldsListItemName,
      showDialogPanel,
    })
)(injectIntl(FieldsPage));



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/FieldsPage.js